import React from 'react';
import { Modal, Box, Typography, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import { getSpotWaitingInfoAll } from '../../../api/golf';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';

const GolfSpotWatingListModal = ({ open, handleClose, spotData }) => {
  const { t } = useTranslation();
  const user = useSelector(state => state.user.user);

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 'auto',
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: '8px',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  };

  // startTime까지 남은 시간(분) 계산
  const calculateRemainingMinutesToStart = startTime => {
    if (!startTime) return 0;
    try {
      const startTimeDate = new Date(startTime);
      const now = new Date();
      const diffMs = startTimeDate - now;
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      return diffMinutes > 0 ? diffMinutes : 0;
    } catch (error) {
      return 0;
    }
  };

  // 회원 이름에서 숫자 제거
  const formatMemberName = memberName => {
    const defaultName = '게스트';
    if (!memberName) return defaultName;
    const nameWithoutNumbers = memberName.replace(/\d+/g, '').trim();
    return nameWithoutNumbers || defaultName;
  };

  // 대기자 정보를 포맷팅
  const formatWaitingMember = waiting => {
    // startTime에서 여유시간을 빼서 실제 이용 시작 시간 계산
    const bufferMinute = waiting.bufferMinute || 0;
    let actualUsageStartTime = waiting.startTime;

    if (actualUsageStartTime && bufferMinute > 0) {
      try {
        const startTimeDate = new Date(actualUsageStartTime);
        const actualStartDate = new Date(startTimeDate.getTime() * 60000);
        actualUsageStartTime = actualStartDate.toISOString();
      } catch (error) {
        // 파싱 실패 시 원본 startTime 사용
      }
    }

    return {
      name: formatMemberName(waiting.memberName),
      waitingMinutes: calculateRemainingMinutesToStart(actualUsageStartTime), // 여유시간 제외한 실제 이용 시작 시간
      reservationId: waiting.reservationId,
      memberId: waiting.memberId,
      memberPhone: waiting.memberPhone,
      startTime: waiting.startTime,
    };
  };

  // API 응답 데이터를 UI에 필요한 형태로 변환
  const transformWaitingListData = (data, targetSpotId, spotNumber) => {
    // 유효하지 않은 데이터인 경우
    if (!data || !Array.isArray(data.spots)) {
      return { waitingList: [], spotNumber: spotNumber };
    }

    // 해당 스팟 찾기
    const matchedSpot = data.spots.find(spot => spot.spotId === targetSpotId);

    // 매칭된 스팟이 없거나 대기자 목록이 없는 경우
    if (!matchedSpot || !Array.isArray(matchedSpot.waitingList)) {
      return { waitingList: [], spotNumber: spotNumber };
    }

    // 대기자 목록 포맷팅
    const formattedWaitingList = matchedSpot.waitingList.map(formatWaitingMember);

    return {
      waitingList: formattedWaitingList,
      spotNumber: matchedSpot.spotNumber || spotNumber,
    };
  };

  // 대기자 목록 조회
  const { data: waitingData } = useQuery({
    queryKey: ['spotWaitingList', user?.token, spotData?.areaId, spotData?.id],
    queryFn: () => getSpotWaitingInfoAll(user.token, spotData.areaId),
    enabled: !!open && !!user?.token && !!spotData?.areaId && !!spotData?.id,
    select: data => transformWaitingListData(data, spotData.id, spotData?.spotNumber || 1),
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchInterval: false,
  });

  const waitingList = waitingData?.waitingList || [];
  const spotNumber = waitingData?.spotNumber || spotData?.spotNumber || 1;
  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={modalStyle}>
        {/* 헤더 */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: '56px',
            px: '32px',
            backgroundColor: '#262626',
            borderRadius: '8px 8px 0 0',
          }}>
          <Typography sx={{ fontSize: '20px', fontWeight: '600', color: '#fff' }}>
            {t('Waiting list status')}
          </Typography>
          <Button
            onClick={handleClose}
            sx={{
              minWidth: '32px',
              width: '32px',
              height: '32px',
              p: 0,
              color: '#fff',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
            }}>
            <CloseIcon />
          </Button>
        </Box>

        {/* 본문 */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', p: '32px', overflowY: 'auto' }}>
          {waitingList.length === 0 ? (
            <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#A6A6A6', textAlign: 'center', py: '32px' }}>
              {t('No waiting members')}
            </Typography>
          ) : (
            waitingList.map((member, index) => (
              <Box
                key={member.reservationId || index}
                sx={{
                  display: 'flex',
                  gap: '16px',
                  height: '64px',
                  p: '8px',
                  border: '1px solid #D9D9D9',
                  borderRadius: '8px',
                }}>
                {/* 타석 번호 */}
                <Box
                  sx={{
                    width: '106px',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px',
                  }}>
                  <Typography sx={{ fontSize: '32px', fontWeight: '700', color: '#A6A6A6' }}>{spotNumber}</Typography>
                </Box>

                {/* 대기자 목록 */}
                <Box
                  sx={{
                    flex: 1,
                    height: '100%',
                    display: 'flex',
                    gap: '16px',
                    alignItems: 'center',
                    px: '16px',
                    overflowX: 'auto',
                  }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                      {/* 회원 이름 */}
                      <Typography sx={{ fontSize: '18px', fontWeight: '500', color: '#000' }}>{member.name}</Typography>
                      {/* 대기 시간 */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '0px' }}>
                        <Typography
                          sx={{
                            fontSize: '20px',
                            fontWeight: '500',
                            color: index === 0 ? '#0F7F85' : '#A6A6A6',
                          }}>
                          {member.waitingMinutes}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '20px',
                            fontWeight: '500',
                            color: index === 0 ? '#0F7F85' : '#A6A6A6',
                          }}>
                          {t('minutes')}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            ))
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default GolfSpotWatingListModal;
